// src/components/layout/MenuCompte.tsx — SCE Issues
// Pastille d'identité dans l'en-tête, et son menu de compte.
//
// ─── Ce qu'il remplace ───
//
// Un tiroir latéral de 300px, avec voile noir à 60 %, servait huit entrées.
// Quatre doublonnaient mot pour mot avec la barre du bas ; Équipements a
// rejoint cette barre. Il ne restait que l'identité, l'administration et la
// déconnexion — trois lignes, pour lesquelles un panneau pleine hauteur et un
// voile opaque étaient hors de proportion.
//
// Ce n'est donc pas un menu de NAVIGATION mais un menu de COMPTE : qui suis-je,
// et comment je sors. La navigation vit entièrement dans la barre du bas.
//
// ─── Pourquoi une pastille d'initiales plutôt qu'une icône ───
//
// Les postes sont partagés en atelier. Voir d'un coup d'œil sous quelle
// identité on est connecté évite qu'un opérateur déclare sous le compte du
// précédent — et la pastille le montre sans qu'on ait à ouvrir quoi que ce soit.

import { lazy, Suspense, useEffect, useId, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ArrowLeftRight, Bell, Bug, CalendarDays, ChevronDown, ClipboardList, Eye, EyeOff, Handshake, LogOut, Palette, ShieldCheck } from 'lucide-react'
import { useAuth } from '@/auth/useAuth'
import { partageDuCompte } from '@/auth/identiteCompte'
import { changerDePersonne } from '@/auth/comptePartage'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { estAdmin } from '@/config/admin.config'
import { FNCT_MASQUEE } from '@/config/fnct.config'
import { accesFnct } from '@/utils/droitsFnct'
import { PresenceAvatars } from '@/components/presence/PresenceAvatars'
import { usePresence } from '@/components/presence/PresenceProvider'
import { ImagePhoto, usePhotoPersonne } from '@/components/personnes/PastillePersonne'
import { useVueRole } from '@/features/vue-role/contexte'
import { initialesDe, nomComplet } from '@/utils/noms'
import { LogoSartorius } from '@/components/ui/Marque'
import { demanderOuverture, useEtatRetour } from '@/features/retour-absence/etat'
import { useExterneConnecte } from '@/features/roles/externeConnecte'
import { libelleMontages } from '@/utils/roles'
import { LogoSociete } from '@/components/ui/LogoSociete'
import { useEnFabrication } from '@/hooks/useEnFabrication'
import { peutAttribuerMontages, useMontages } from '@/hooks/useMontages'
import { nombreAAttribuer } from '@/utils/montages'

// Chargé à la demande, et seulement si le compte RÉEL a droit à la bascule :
// un autre utilisateur ne télécharge jamais le sélecteur ni les profils.
const SelecteurVueRole = lazy(() => import('@/features/vue-role/SelecteurVueRole'))
const FenetreVueRole = lazy(() => import('@/features/vue-role/FenetreVueRole'))

/**
 * Initiales affichées dans la pastille.
 *
 * Deux lettres au plus : « Guillaume Delavault » → GD. Un UPN de guest B2B
 * n'a pas toujours de nom exploitable, d'où le repli sur l'e-mail puis sur un
 * point d'interrogation — la pastille ne doit jamais rendre une case vide.
 */
function initiales(nom: string | undefined, email: string | undefined): string {
  return initialesDe((nom ?? '').trim() || (email ?? '').trim())
}

export function MenuCompte(): JSX.Element | null {
  const navigate = useNavigate()
  const location = useLocation()
  const { account, logout } = useAuth()
  // Appareil partagé (iPad de l'atelier, 07/10) : sur le compte RÉEL, comme la
  // bascule de rôle — on passe d'une personne à l'autre ici.
  const partage = partageDuCompte(account)
  const { user, isCoordinateur, isFournisseur } = useCurrentUser()
  const externe = useExterneConnecte()
  const retour = useEtatRetour()
  // La photo remplace les initiales quand elle arrive (26/10) : on voit encore
  // d'un coup d'œil sous quel compte on est.
  const photo = usePhotoPersonne(user?.upn)
  const presence = usePresence()
  // Droit à la bascule : décidé sur le compte RÉEL (piège de la récursion
  // d'identité) — en vue Opérateur, `user` n'est plus administrateur, mais
  // le sélecteur doit rester là pour revenir.
  const { autorise: bascule } = useVueRole()
  const [ouvert, setOuvert] = useState(false)
  // « Voir en tant que · Quelqu'un d'autre… » (26/10) : hors du menu, qui se referme.
  const [choixPersonne, setChoixPersonne] = useState(false)
  const conteneur = useRef<HTMLDivElement>(null)
  const bouton = useRef<HTMLButtonElement>(null)
  const idMenu = useId()

  // Fermeture au clic extérieur et à Échap. Les deux sont indispensables : le
  // menu n'a pas de voile, donc rien n'intercepte les clics à sa place.
  useEffect(() => {
    if (!ouvert) return

    function auClic(e: MouseEvent): void {
      if (!conteneur.current?.contains(e.target as Node)) setOuvert(false)
    }
    function auClavier(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        setOuvert(false)
        // Le focus doit revenir sur le bouton, sinon il retombe sur <body> et
        // la navigation au clavier repart du début de la page.
        bouton.current?.focus()
      }
    }

    document.addEventListener('mousedown', auClic)
    document.addEventListener('keydown', auClavier)
    return () => {
      document.removeEventListener('mousedown', auClic)
      document.removeEventListener('keydown', auClavier)
    }
  }, [ouvert])

  // Une navigation ferme le menu — sans quoi il resterait ouvert par-dessus la
  // page d'arrivée.
  useEffect(() => setOuvert(false), [location.pathname])

  if (!user) return null

  // Sur l'identité AFFICHÉE : en vue coordinateur, pas de lien Administration,
  // exactement comme chez lui.
  const admin = estAdmin(user.upn)

  function aller(chemin: string): void {
    setOuvert(false)
    navigate(chemin)
  }

  return (
    // 12 px avant la pile : les icônes voisines (Rafraîchir, cloche) ont 12 px de
    // vide de chaque côté de leur dessin ; la photo, elle, remplit sa cible. Sans
    // cet écart, elle collait à la cloche (03/11).
    <div className="flex items-center gap-1" style={{ marginLeft: 12 }}>
    <PresenceAvatars />
    <div ref={conteneur} style={{ position: 'relative' }}>
      <button
        ref={bouton}
        type="button"
        onClick={() => setOuvert((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={ouvert}
        aria-controls={ouvert ? idMenu : undefined}
        aria-label={`Compte de ${user.nom || user.email}`}
        title={user.nom || user.email}
        className="flex items-center justify-center rounded-full"
        style={{
          width: 36,
          height: 36,
          flexShrink: 0,
          padding: 0,
          overflow: 'hidden',
          border: photo ? 'none' : '1px solid var(--color-border)',
          backgroundColor: 'var(--color-bg-card)',
          color: 'var(--color-text-primary)',
          fontSize: 'var(--font-size-info)',
          fontWeight: 600,
          letterSpacing: '0.02em',
          cursor: 'pointer',
        }}
      >
        {photo ? <ImagePhoto src={photo} /> : initiales(user.nom, user.email)}
      </button>
      {/* Un menu derrière la photo (03/11) : un chevron en médaillon dans le coin,
          comme l'icône sur la photo de l'activité. La photo garde son rond nu
          (jamais de bordure ni d'anneau). Hors du bouton, qui rogne. */}
      <span
        aria-hidden="true"
        className="flex items-center justify-center"
        style={{
          position: 'absolute',
          right: -3,
          bottom: -3,
          width: 16,
          height: 16,
          borderRadius: 8,
          backgroundColor: 'var(--color-white)',
          color: 'var(--color-black)',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
          pointerEvents: 'none',
        }}
      >
        <ChevronDown size={12} strokeWidth={3} style={{ transform: ouvert ? 'rotate(180deg)' : undefined, transition: 'transform 0.15s' }} />
      </span>

      {ouvert && (
        <div
          id={idMenu}
          role="menu"
          aria-label="Menu du compte"
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            width: 248,
            backgroundColor: 'var(--color-bg-card)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-xl)',
            // Aucun voile : la page derrière reste lisible, on ne perd pas sa
            // place. L'ombre suffit à détacher le panneau.
            boxShadow: '0 6px 24px rgba(0, 0, 0, 0.14)',
            // Jamais plus haut que l'écran : au-delà, le menu défile (27/10).
            maxHeight: 'calc(100dvh - var(--spacing-header-h) - 16px)',
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            zIndex: 50,
          }}
        >
          {/* Identité */}
          <div
            style={{
              padding: '12px 14px',
              borderBottom: '1px solid var(--color-border)',
            }}
          >
            <div
              className="truncate"
              style={{ fontSize: 'var(--font-size-copy2)', fontWeight: 500 }}
            >
              {user.nom}
            </div>
            <div
              className="truncate"
              style={{
                fontSize: 'var(--font-size-info)',
                color: 'var(--color-text-secondary)',
              }}
            >
              {user.email}
            </div>
            {partage && (
              <div
                className="truncate"
                style={{ fontSize: 'var(--font-size-info)', color: 'var(--color-text-secondary)' }}
              >
                {partage.libelle}
              </div>
            )}
            {/* Externe reconnu (29/10) : sa société, et ce qu'elle fait pour l'usine. */}
            {externe && (externe.societe || externe.externe.societe) && (
              <div
                className="flex items-center"
                style={{ gap: 6, marginTop: 2, fontSize: 'var(--font-size-info)', color: 'var(--color-text-secondary)', minWidth: 0 }}
              >
                <LogoSociete id={externe.societe?.logo} hauteur={16} />
                <span className="truncate">
                  {[externe.societe?.nom ?? externe.externe.societe, externe.societe ? libelleMontages(externe.societe.montages) : '']
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </div>
            )}
          </div>

          {/* Appareil partagé : passer à l'autre personne (la page repart à son nom). */}
          {partage &&
            partage.personnes
              .filter((p) => p.upn.toLowerCase() !== user.upn.toLowerCase())
              .map((p) => (
                <ItemCompte
                  key={p.upn}
                  Icon={ArrowLeftRight}
                  label={`Passer à ${nomComplet(p.nom)}`}
                  onClick={() => changerDePersonne(partage.objectId, p.upn)}
                />
              ))}
          {partage && <Separateur />}

          {/* Le récap de l'absence (28/10) : trois jours, tant qu'il reste quelque chose à faire. */}
          {retour.disponible && retour.upn === user.upn && (
            <ItemCompte
              Icon={CalendarDays}
              label="Le récap de votre absence"
              onClick={() => {
                demanderOuverture()
                aller('/')
              }}
            />
          )}

          {/* Administration — réservée aux UPN de admin.config.ts */}
          {admin && (
            <ItemCompte
              Icon={ShieldCheck}
              label="Administration"
              onClick={() => aller('/admin')}
            />
          )}

          {/* FNC-T (chantier du 06/10) — administrateur et testeurs pendant la construction. */}
          {accesFnct(user.upn) && !FNCT_MASQUEE && (
            <ItemCompte
              Icon={ClipboardList}
              label="FNC-T"
              onClick={() => aller('/fnct')}
            />
          )}

          {/* Canaux de notification (Lot 10 bis) — pour tout le monde. */}
          <ItemCompte
            Icon={Bell}
            label="Mes notifications"
            onClick={() => aller('/notifications')}
          />
          {/* Ce que vous suivez (03/11) — internes : un fournisseur ne suit rien. */}
          {!isFournisseur && (
            <ItemCompte
              Icon={Eye}
              label="Ce que vous suivez"
              onClick={() => aller('/suivis')}
            />
          )}

          {/* Apparaître non connecté (26/10) — compte réel administrateur seulement (PresenceProvider). */}
          {presence.disponible && (
            <BasculeCompte
              Icon={EyeOff}
              label="Apparaître non connecté"
              detail="Les autres ne vous voient plus parmi les connectés"
              actif={presence.invisible}
              onChange={(v) => void presence.changerInvisible(v)}
            />
          )}

          {/* Montage sous-traité (29/10) — tout coordinateur ou suppléant, et l'administrateur. */}
          {peutAttribuerMontages(user.upn) && <ItemMontages onClick={() => aller('/montages')} />}

          {/* Couleurs des métiers (Lot 8) — chaque coordinateur règle la
              sienne, l'administrateur toutes. Identité affichée, comme
              Administration. */}
          {(admin || isCoordinateur) && (
            <ItemCompte
              Icon={Palette}
              label="Couleurs des métiers"
              onClick={() => aller('/couleurs-metiers')}
            />
          )}

          {/* Bascule de rôle (Lot 7) — compte réel administrateur. */}
          {bascule && (
            <>
              <Separateur />
              <Suspense fallback={null}>
                <SelecteurVueRole
                  onChoisi={() => setOuvert(false)}
                  onQuelquUn={() => {
                    setOuvert(false)
                    setChoixPersonne(true)
                  }}
                />
              </Suspense>
            </>
          )}

          {/* Outils de développement — absents du build de production */}
          {import.meta.env.DEV && (
            <>
              <Separateur />
              <ItemCompte Icon={Bug} label="Debug" onClick={() => aller('/debug')} />
            </>
          )}

          <Separateur />
          <ItemCompte
            Icon={LogOut}
            label="Se déconnecter"
            danger
            onClick={() => {
              setOuvert(false)
              void logout()
            }}
          />

          {/* Pied : l'outil signé (maquette du 29/09). */}
          <div
            className="flex items-center justify-between"
            style={{
              gap: 12,
              padding: '12px 14px',
              borderTop: '1px solid var(--color-border)',
              backgroundColor: 'var(--color-gray-5)',
            }}
          >
            <LogoSartorius hauteur={11} />
            <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
              SCE Issues · Pompey
            </span>
          </div>
        </div>
      )}
    </div>
    {choixPersonne && (
      <Suspense fallback={null}>
        <FenetreVueRole onFermer={() => setChoixPersonne(false)} />
      </Suspense>
    )}
    </div>
  )
}

// ─── Sous-composants ───

function Separateur(): JSX.Element {
  return <div style={{ height: 1, backgroundColor: 'var(--color-border)' }} />
}

/** Un réglage tout ou rien du menu : un interrupteur, le menu reste ouvert. */
function BasculeCompte({
  Icon,
  label,
  detail,
  actif,
  onChange,
}: {
  Icon: typeof LogOut
  label: string
  detail: string
  actif: boolean
  onChange: (actif: boolean) => void
}): JSX.Element {
  return (
    <button
      type="button"
      role="menuitemcheckbox"
      aria-checked={actif}
      onClick={() => onChange(!actif)}
      className="flex items-center gap-3 w-full text-left"
      style={{
        padding: '11px 14px',
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        color: 'var(--color-text-primary)',
      }}
    >
      <Icon size={18} strokeWidth={2} style={{ flexShrink: 0 }} />
      <span className="flex flex-col" style={{ flex: 1, minWidth: 0, gap: 2 }}>
        <span style={{ fontSize: 'var(--font-size-copy2)', fontWeight: 500 }}>{label}</span>
        <span style={{ fontSize: 'var(--font-size-info)', color: 'var(--color-text-secondary)' }}>{detail}</span>
      </span>
      <span
        aria-hidden="true"
        style={{
          position: 'relative',
          width: 36,
          height: 20,
          flexShrink: 0,
          borderRadius: 10,
          backgroundColor: actif ? 'var(--color-black)' : 'var(--color-gray-30)',
          transition: 'background-color 0.15s ease',
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 2,
            left: actif ? 18 : 2,
            width: 16,
            height: 16,
            borderRadius: 8,
            backgroundColor: 'var(--color-white)',
            transition: 'left 0.15s ease',
          }}
        />
      </span>
    </button>
  )
}

/**
 * « Montage sous-traité », avec le nombre d'équipements en fabrication dont un
 * montage reste à attribuer. Monté à l'ouverture du menu seulement : les deux
 * lectures (une par session) n'ont lieu que pour qui ouvre son menu.
 */
function ItemMontages({ onClick }: { onClick: () => void }): JSX.Element {
  const { liste } = useEnFabrication()
  const { montages, chargement, erreur } = useMontages()
  const reste = liste && !chargement && !erreur ? nombreAAttribuer(montages, liste.numerosSerie) : 0
  return <ItemCompte Icon={Handshake} label="Montage sous-traité" pastille={reste > 0 ? `${reste} à attribuer` : undefined} onClick={onClick} />
}

function ItemCompte({
  Icon,
  label,
  onClick,
  danger,
  pastille,
}: {
  Icon: typeof LogOut
  label: string
  onClick: () => void
  danger?: boolean
  /** Petit compteur en bout de ligne (« 6 à attribuer »). */
  pastille?: string
}): JSX.Element {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="flex items-center gap-3 w-full text-left"
      style={{
        padding: '11px 14px',
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        fontSize: 'var(--font-size-copy2)',
        fontWeight: 500,
        color: danger ? 'var(--color-error)' : 'var(--color-text-primary)',
      }}
    >
      <Icon size={18} strokeWidth={2} style={{ flexShrink: 0 }} />
      <span style={{ flex: 1, minWidth: 0 }}>{label}</span>
      {pastille && (
        <span
          style={{
            flexShrink: 0,
            padding: '2px 6px',
            borderRadius: 6,
            backgroundColor: 'var(--color-black)',
            color: 'var(--color-white)',
            fontSize: 11,
            lineHeight: '14px',
            fontWeight: 500,
          }}
        >
          {pastille}
        </span>
      )}
    </button>
  )
}
